/**
 * FikoInjectionMoldingCost - Enjeksiyon Kaliplama Maliyet Hesaplama
 *
 * Kalip maliyeti, malzeme, cevrim suresi, makine tonaji
 * bazli detayli maliyet hesaplama.
 */

import { FikoMaterialDB, MaterialProperties } from './FikoMaterialDB';
import { CostLineItem } from './FikoCostEngine';

export interface InjectionMoldingCostInput {
    partGeometry: {
        volumeCm3: number;
        surfaceAreaCm2: number;
        boundingBox: { x: number; y: number; z: number };
        complexityScore: number;
        holeCount: number;
        undercutCount: number;
        ribCount: number;
    };
    material: MaterialProperties;
    batchSize: number;
    annualVolume: number;
    cavityCount: number;
    moldClass?: 'prototype' | 'production_low' | 'production_mid' | 'production_high';
    runnerType?: 'cold' | 'hot';
    texturedSurface?: boolean;
}

export interface InjectionMoldingCostResult {
    costPerPart: number;
    costPerKg: number;
    breakdown: CostLineItem[];
    moldCost: number;
    cycleTimeSeconds: number;
    clampTonnage: number;
    notes: string[];
    notesTR: string[];
}

export class FikoInjectionMoldingCost {
    private materialDB: FikoMaterialDB;

    constructor(materialDB: FikoMaterialDB) {
        this.materialDB = materialDB;
    }

    calculate(input: InjectionMoldingCostInput): InjectionMoldingCostResult {
        const { material, partGeometry, batchSize, annualVolume, cavityCount } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        const partWeightKg = partGeometry.volumeCm3 * material.density;
        const partWeightGrams = partWeightKg * 1000;

        // 1. Kalip maliyeti
        const moldClass = input.moldClass || this.suggestMoldClass(annualVolume);
        const moldCost = this.estimateMoldCost(partGeometry, cavityCount, moldClass, input);
        const moldLife = this.getMoldLife(moldClass);
        const amortizedMoldCost = moldCost / Math.min(annualVolume * 3, moldLife); // 3 yil amorti veya kalip omru

        breakdown.push({
            category: 'tooling',
            description: `${moldClass} mold, ${cavityCount}-cavity ($${moldCost.toFixed(0)} over ${Math.min(annualVolume * 3, moldLife)} parts)`,
            descriptionTR: `${this.getMoldClassTR(moldClass)} kalip, ${cavityCount} kavite ($${moldCost.toFixed(0)}, ${Math.min(annualVolume * 3, moldLife)} parcaya amorti)`,
            quantity: 1,
            unitCost: amortizedMoldCost,
            totalCost: amortizedMoldCost,
            unit: 'adet'
        });

        // 2. Malzeme maliyeti (parca + yolluk fire)
        const runnerType = input.runnerType || (annualVolume > 50000 ? 'hot' : 'cold');
        const runnerWastePercent = runnerType === 'hot' ? 2 : 15;
        const shotWeightGrams = partWeightGrams * cavityCount * (1 + runnerWastePercent / 100);
        const materialCostPerPart = (partWeightGrams * (1 + runnerWastePercent / 100) / 1000) * material.pricePerKg;

        breakdown.push({
            category: 'material',
            description: `${material.name} (${partWeightGrams.toFixed(1)}g + ${runnerWastePercent}% runner waste, ${runnerType} runner)`,
            descriptionTR: `${material.nameTR} (${partWeightGrams.toFixed(1)}g + %${runnerWastePercent} yolluk fire, ${runnerType === 'hot' ? 'sicak' : 'soguk'} yolluk)`,
            quantity: partWeightGrams * (1 + runnerWastePercent / 100) / 1000,
            unitCost: material.pricePerKg,
            totalCost: materialCostPerPart,
            unit: 'kg'
        });

        // 3. Cevrim suresi hesabi
        const cycleTimeSeconds = this.estimateCycleTime(partGeometry, material);
        const cycleTimePerPartSeconds = cycleTimeSeconds / cavityCount;

        // 4. Makine saat ucreti (tonaja gore)
        const clampTonnage = this.estimateClampTonnage(partGeometry, material, cavityCount);
        const machineHourlyRate = this.getMachineRateByTonnage(clampTonnage);
        const machineCostPerPart = (cycleTimePerPartSeconds / 3600) * machineHourlyRate;

        breakdown.push({
            category: 'machine',
            description: `${clampTonnage.toFixed(0)}T machine (cycle ${cycleTimeSeconds.toFixed(1)}s / ${cavityCount} cav = ${cycleTimePerPartSeconds.toFixed(1)}s/part, $${machineHourlyRate}/hr)`,
            descriptionTR: `${clampTonnage.toFixed(0)}T makine (cevrim ${cycleTimeSeconds.toFixed(1)}sn / ${cavityCount} kav = ${cycleTimePerPartSeconds.toFixed(1)}sn/parca, $${machineHourlyRate}/saat)`,
            quantity: cycleTimePerPartSeconds / 3600,
            unitCost: machineHourlyRate,
            totalCost: machineCostPerPart,
            unit: 'saat'
        });

        // 5. Sicak yolluk maliyeti (varsa, kalip maliyetine ek)
        if (runnerType === 'hot') {
            const hotRunnerCost = cavityCount * 2500; // ~$2500 per drop
            const amortizedHR = hotRunnerCost / Math.min(annualVolume * 3, moldLife);
            breakdown.push({
                category: 'tooling',
                description: `Hot runner system (${cavityCount} drops, $${hotRunnerCost})`,
                descriptionTR: `Sicak yolluk sistemi (${cavityCount} nokta, $${hotRunnerCost})`,
                quantity: 1,
                unitCost: amortizedHR,
                totalCost: amortizedHR,
                unit: 'adet'
            });
        }

        // 6. Yan cekici maliyeti (undercut varsa)
        if (partGeometry.undercutCount > 0) {
            const sideActionCost = partGeometry.undercutCount * 3000; // ~$3000 per side action
            const amortizedSA = sideActionCost / Math.min(annualVolume * 3, moldLife);
            breakdown.push({
                category: 'tooling',
                description: `${partGeometry.undercutCount} side action(s) ($${sideActionCost})`,
                descriptionTR: `${partGeometry.undercutCount} yan cekici ($${sideActionCost})`,
                quantity: partGeometry.undercutCount,
                unitCost: amortizedSA / partGeometry.undercutCount,
                totalCost: amortizedSA,
                unit: 'adet'
            });
            notes.push(`${partGeometry.undercutCount} side actions add $${sideActionCost} to mold cost`);
            notesTR.push(`${partGeometry.undercutCount} yan cekici kalip maliyetine $${sideActionCost} ekler`);
        }

        // 7. Son islemler (yolluk kesme, kalite kontrol)
        const postProcessCost = runnerType === 'cold' ? 0.15 : 0.05; // Cold runner needs gate trimming
        breakdown.push({
            category: 'post_processing',
            description: 'Gate trimming, inspection',
            descriptionTR: 'Yolluk kesme, kontrol',
            quantity: 1,
            unitCost: postProcessCost,
            totalCost: postProcessCost,
            unit: 'adet'
        });

        // 8. Iscilik
        const laborCostPerPart = (cycleTimePerPartSeconds / 3600) * 18 * 0.5; // Operator at 50% attention
        breakdown.push({
            category: 'labor',
            description: 'Operator labor',
            descriptionTR: 'Operator isciligi',
            quantity: cycleTimePerPartSeconds / 3600,
            unitCost: 18 * 0.5,
            totalCost: laborCostPerPart,
            unit: 'saat'
        });

        // 9. Setup (amorti)
        const setupCost = (2 * machineHourlyRate) / batchSize; // ~2 hour mold change
        breakdown.push({
            category: 'setup',
            description: `Mold setup (~2hr, / ${batchSize} parts)`,
            descriptionTR: `Kalip hazirlik (~2saat, / ${batchSize} parca)`,
            quantity: 1,
            unitCost: setupCost,
            totalCost: setupCost,
            unit: 'adet'
        });

        // Toplam
        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        const costPerKg = partWeightKg > 0 ? costPerPart / partWeightKg : 0;

        notes.push(`Cycle time: ${cycleTimeSeconds.toFixed(1)}s, Clamp: ${clampTonnage.toFixed(0)}T`);
        notes.push(`Mold cost: $${moldCost.toFixed(0)} (${moldClass})`);
        notes.push(`Parts/hour: ${Math.floor(3600 / cycleTimeSeconds * cavityCount)}`);
        notesTR.push(`Cevrim suresi: ${cycleTimeSeconds.toFixed(1)}sn, Kapama kuvveti: ${clampTonnage.toFixed(0)}T`);
        notesTR.push(`Kalip maliyeti: $${moldCost.toFixed(0)} (${this.getMoldClassTR(moldClass)})`);
        notesTR.push(`Saat basi parca: ${Math.floor(3600 / cycleTimeSeconds * cavityCount)}`);

        return {
            costPerPart: Math.round(costPerPart * 100) / 100,
            costPerKg: Math.round(costPerKg * 100) / 100,
            breakdown,
            moldCost,
            cycleTimeSeconds,
            clampTonnage,
            notes,
            notesTR,
        };
    }

    private suggestMoldClass(annualVolume: number): string {
        if (annualVolume < 500) return 'prototype';
        if (annualVolume < 10000) return 'production_low';
        if (annualVolume < 100000) return 'production_mid';
        return 'production_high';
    }

    private estimateMoldCost(geom: any, cavities: number, moldClass: string, input: any): number {
        const bbVol = geom.boundingBox.x * geom.boundingBox.y * geom.boundingBox.z;
        const sizeFactor = Math.pow(bbVol / 1e6, 0.45); // Normalize

        const baseCosts: Record<string, number> = {
            prototype: 3000,        // Aluminum mold
            production_low: 8000,   // P20 steel
            production_mid: 25000,  // H13 hardened
            production_high: 60000, // H13 + premium finish
        };

        let cost = (baseCosts[moldClass] || 15000) * sizeFactor;

        // Cavity multiplier (not linear - shared components)
        cost *= 1 + (cavities - 1) * 0.6;

        // Complexity
        cost *= 1 + (geom.complexityScore / 100) * 1.5;

        // Texture
        if (input.texturedSurface) cost *= 1.15;

        return cost;
    }

    private getMoldLife(moldClass: string): number {
        const lives: Record<string, number> = {
            prototype: 5000,
            production_low: 100000,
            production_mid: 500000,
            production_high: 1000000,
        };
        return lives[moldClass] || 100000;
    }

    private estimateCycleTime(geom: any, material: MaterialProperties): number {
        // Cycle = fill + pack + cool + eject
        const wallThickness = 2.0; // Default assumption mm
        const fillTime = 1.0 + geom.volumeCm3 * 0.1; // Base 1s + volume factor
        const packTime = 2.0;
        // Cooling dominates: proportional to wall thickness squared
        const coolTime = wallThickness * wallThickness * 2.5 * (material.thermalConductivity < 0.2 ? 1.5 : 1.0);
        const ejectTime = 1.5;
        const moldOpenClose = 3.0;

        return fillTime + packTime + coolTime + ejectTime + moldOpenClose;
    }

    private estimateClampTonnage(geom: any, material: MaterialProperties, cavities: number): number {
        // Projected area x injection pressure
        const projectedAreaCm2 = (geom.boundingBox.x * geom.boundingBox.y) / 100; // mm2 to cm2
        const totalProjectedArea = projectedAreaCm2 * cavities;
        const injectionPressure = 350; // kg/cm2 typical for engineering plastics
        const safetyFactor = 1.2;
        const tonnage = (totalProjectedArea * injectionPressure * safetyFactor) / 1000; // to metric tons
        // Round up to standard machine sizes
        const stdSizes = [50, 80, 100, 150, 200, 300, 450, 650, 850, 1000, 1500, 2000, 3000];
        return stdSizes.find(s => s >= tonnage) || tonnage;
    }

    private getMachineRateByTonnage(tonnage: number): number {
        if (tonnage <= 100) return 35;
        if (tonnage <= 200) return 45;
        if (tonnage <= 400) return 60;
        if (tonnage <= 700) return 80;
        if (tonnage <= 1000) return 100;
        if (tonnage <= 2000) return 140;
        return 180;
    }

    private getMoldClassTR(moldClass: string): string {
        const names: Record<string, string> = {
            prototype: 'Prototip',
            production_low: 'Dusuk Hacim Uretim',
            production_mid: 'Orta Hacim Uretim',
            production_high: 'Yuksek Hacim Uretim',
        };
        return names[moldClass] || moldClass;
    }
}
